// GradingStatusBreakdownModal — click-through from the Grading Pipeline
// widget. Shows exactly how a domain's calls split across every status
// bucket for the currently-selected window, plus the last 5 regrade
// jobs (start time, target, status, finish time) so ops has full
// visibility into "why is my percentage what it is".
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Play, CheckCircle2, AlertTriangle, RotateCcw, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  analyzed:      { label: 'Graded (analyzed)',    tone: 'bg-emerald-500' },
  escalated:     { label: 'Graded (escalated)',   tone: 'bg-teal-500' },
  pending:       { label: 'Pending / in queue',   tone: 'bg-amber-500' },
  skipped_short: { label: 'Skipped — too short',  tone: 'bg-slate-500' },
  no_transcript: { label: 'No transcript',        tone: 'bg-slate-400' },
  no_transcript_pending_retry: { label: 'No transcript — pending retry', tone: 'bg-slate-400' },
};

const JobIcon = ({ status }) => {
  if (status === 'completed')  return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />;
  if (status === 'running')    return <Play className="w-3.5 h-3.5 text-amber-400" />;
  if (status === 'superseded') return <RotateCcw className="w-3.5 h-3.5 text-white/40" />;
  return <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />;
};

const formatETA = (secs) => {
  if (secs == null) return null;
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
};

// "green_brain_discovery" -> "Green Brain Discovery"
const humanizeCriterion = (key) =>
  (key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const DeltaBadge = ({ delta }) => {
  if (delta == null) return <span className="text-white/40 tabular-nums">—</span>;
  const positive = delta > 0.05;
  const negative = delta < -0.05;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const tone = positive ? 'text-emerald-300' : negative ? 'text-rose-300' : 'text-white/50';
  const sign = delta > 0 ? '+' : '';
  return (
    <span className={`inline-flex items-center gap-1 tabular-nums ${tone}`}>
      <Icon className="w-3 h-3" />{sign}{delta.toFixed(2)}
    </span>
  );
};

// Convert a Monday-of-week `YYYY-MM-DD` (as returned by the sparkline
// backend) into the ISO-week string `YYYY-W##` that /api/calls?week=…
// expects. Falls back to null when parsing fails so the caller can
// gracefully skip navigation instead of blowing up.
const mondayToISOWeek = (mondayIso) => {
  if (!mondayIso) return null;
  const d = new Date(`${mondayIso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  // Nearest Thursday of the ISO week — required for the ISO-8601 rule
  // that week 1 is the week containing the first Thursday of the year.
  d.setUTCDate(d.getUTCDate() + 3);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
};

const CoachingImpactSection = ({ impact, domainId, onNavigate }) => {
  if (!impact) return null;
  if ((impact.records_total || 0) === 0 && !(impact.weekly_avg_grades || []).some(w => w.avg_score != null)) return null;
  const noData = (impact.records_with_data || 0) === 0;
  const hasSparkline = (impact.weekly_avg_grades || []).some(w => w.avg_score != null);
  return (
    <div className="mt-5 pt-4 border-t border-white/10" data-testid="coaching-impact-section">
      <p className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-2">
        Coaching impact
        <span className="text-white/30 normal-case ml-1">
          · {impact.records_total} record{impact.records_total === 1 ? '' : 's'}
          {impact.records_with_data > 0 && <> · {impact.records_with_data} with re-assessment data</>}
        </span>
      </p>
      {impact.records_total === 0 ? (
        <p className="text-white/40 italic text-xs mb-3">No coaching records in this window — trend is showing grading data only.</p>
      ) : noData ? (
        <p className="text-white/40 italic text-xs">
          Coaching records exist but no re-assessment calls have been graded yet — check back after agents take their next scored calls.
        </p>
      ) : (
        <>
          {/* Baseline → After summary */}
          <div className="grid grid-cols-3 gap-3 text-center mb-3">
            <div>
              <p className="text-[10px] text-white/40 uppercase">Baseline</p>
              <p className="text-white font-semibold tabular-nums text-sm">{impact.avg_baseline?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] text-white/40 uppercase">After coaching</p>
              <p className="text-white font-semibold tabular-nums text-sm">{impact.avg_after?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] text-white/40 uppercase">Net delta</p>
              <p className="font-semibold text-sm"><DeltaBadge delta={impact.avg_delta} /></p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] mb-3">
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-semibold tabular-nums">
              +{impact.improved_count} improved
            </span>
            <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 font-semibold tabular-nums">
              −{impact.regressed_count} regressed
            </span>
          </div>
          {impact.top_focus_criteria?.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] uppercase text-white/40 font-semibold mb-1">Top focus criteria by lift</p>
              <div className="space-y-1">
                {impact.top_focus_criteria.map(fc => (
                  <div key={fc.criterion} className="flex items-center justify-between text-xs" data-testid={`impact-focus-${fc.criterion}`}>
                    <span className="text-white/70 truncate mr-2">{humanizeCriterion(fc.criterion)}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-white/40 text-[10px]">n={fc.n}</span>
                      <DeltaBadge delta={fc.delta_avg} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {hasSparkline && <WeeklyGradeSparkline weeks={impact.weekly_avg_grades} domainId={domainId} onNavigate={onNavigate} />}
    </div>
  );
};

const WeeklyGradeSparkline = ({ weeks, domainId, onNavigate }) => {
  // Layout constants — chosen so the sparkline stays legible inside the
  // slim drill-in modal (500px content width). We reserve 20px of vertical
  // headroom for tooltips on hover.
  const W = 320;
  const H = 60;
  const PAD_X = 6;
  const PAD_Y = 6;

  if (!weeks || weeks.length === 0) return null;
  const values = weeks.map(w => w.avg_score);
  const nonNulls = values.filter(v => v != null);
  if (nonNulls.length < 2) return null; // Need at least 2 points to draw a line

  const min = Math.min(...nonNulls);
  const max = Math.max(...nonNulls);
  const span = max - min || 1; // Avoid divide-by-zero when all points equal.
  const dx = (W - 2 * PAD_X) / (weeks.length - 1);

  const scaleY = (v) => H - PAD_Y - ((v - min) / span) * (H - 2 * PAD_Y);
  const points = weeks.map((w, i) => ({
    x: PAD_X + i * dx,
    y: w.avg_score != null ? scaleY(w.avg_score) : null,
    week: w.week_start,
    score: w.avg_score,
    n: w.n,
  }));

  // Build a poly-line path skipping null points (draws segments between
  // consecutive defined values). Simpler than trying to interpolate.
  const segments = [];
  let current = [];
  points.forEach(p => {
    if (p.y == null) {
      if (current.length > 1) segments.push(current);
      current = [];
    } else {
      current.push(p);
    }
  });
  if (current.length > 1) segments.push(current);

  const first = nonNulls[0];
  const last = nonNulls[nonNulls.length - 1];
  const overallDelta = last - first;
  const strokeTone = overallDelta > 1 ? '#10b981' : overallDelta < -1 ? '#f43f5e' : '#c8974a';

  const canDrill = typeof onNavigate === 'function' && !!domainId;
  const handlePointClick = (weekStart) => {
    if (!canDrill) return;
    const isoWeek = mondayToISOWeek(weekStart);
    if (!isoWeek) return;
    // Master-admin `/calls` endpoint accepts `domain_id` + `week` as
    // query params — the CallsListPage forwards `week` verbatim into
    // its server-side ISO-week filter. Preserving `status=analyzed`
    // etc. isn't necessary here — the manager wants ALL calls that
    // shaped that week's grade, not a status-narrowed slice.
    onNavigate(`/calls?domain_id=${encodeURIComponent(domainId)}&week=${encodeURIComponent(isoWeek)}`);
  };

  return (
    <div className="mt-1" data-testid="coaching-impact-sparkline">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] uppercase text-white/40 font-semibold">
          Domain grade trend · last 12 weeks
          {canDrill && <span className="normal-case ml-1 text-white/30">· click a point to see that week&apos;s calls</span>}
        </p>
        <span className="text-[10px] tabular-nums text-white/60">
          {first?.toFixed(1)} → {last?.toFixed(1)} <DeltaBadge delta={overallDelta} />
        </span>
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block" aria-label="weekly average grade sparkline">
        {/* Baseline reference at midpoint of range */}
        <line x1={PAD_X} x2={W - PAD_X} y1={H / 2} y2={H / 2} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 3" />
        {segments.map((seg, i) => (
          <polyline
            key={i}
            fill="none"
            stroke={strokeTone}
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={seg.map(p => `${p.x},${p.y}`).join(' ')}
          />
        ))}
        {points.map((p, i) => p.y != null && (
          <g key={i}>
            {/* Larger hit-target so the 2-px dot is clickable without asking
                the user to pixel-hunt. Uses a near-invisible fill instead of
                fill="transparent" so Playwright / a11y tools treat it as a
                real interactive element and can activate it via `.click()`. */}
            {canDrill && (
              <circle
                cx={p.x}
                cy={p.y}
                r="8"
                fill="white"
                fillOpacity="0.01"
                className="cursor-pointer hover:fill-white hover:fill-opacity-10"
                onClick={() => handlePointClick(p.week)}
                data-testid={`sparkline-hit-${p.week}`}
              >
                <title>{p.week}: avg {p.score?.toFixed(1)} (n={p.n}) — click to view these calls</title>
              </circle>
            )}
            <circle
              cx={p.x}
              cy={p.y}
              r="2"
              fill={strokeTone}
              pointerEvents="none"
              data-testid={`sparkline-point-${p.week}`}
            >
              {!canDrill && <title>{p.week}: avg {p.score?.toFixed(1)} (n={p.n})</title>}
            </circle>
          </g>
        ))}
      </svg>
      <div className="flex justify-between text-[9px] text-white/30 mt-0.5 tabular-nums">
        <span>{weeks[0]?.week_start.slice(5)}</span>
        <span>{weeks[weeks.length - 1]?.week_start.slice(5)}</span>
      </div>
    </div>
  );
};

export const GradingStatusBreakdownModal = ({ token, domainId, windowDays, onClose }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!domainId) return;
    setData(null);
    axios.get(`${API}/admin/domains/${domainId}/status-breakdown`, {
      headers: { Authorization: `Bearer ${token}` },
      params: windowDays ? { days: windowDays } : {},
    })
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [domainId, windowDays, token]);

  const handleSparklineNavigate = (path) => {
    // Close the modal FIRST so react-router doesn't render both the
    // modal backdrop and the new page for a frame — otherwise the
    // grading list momentarily inherits the modal's overlay.
    if (typeof onClose === 'function') onClose();
    navigate(path);
  };

  if (!domainId) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="grading-breakdown-modal"
    >
      <div
        className="bg-slate-900 border border-white/10 rounded-xl max-w-lg w-full p-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-teal-400 font-semibold">Status breakdown</p>
            <h3 className="text-white text-lg font-bold">{data?.company_name || data?.yabbit_domain || '…'}</h3>
            <p className="text-white/40 text-xs mt-0.5">
              {windowDays ? `Last ${windowDays} days` : 'All time'}
              {data?.total != null && <> · {data.total} calls</>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white p-1"
            data-testid="grading-breakdown-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!data ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-white/30 animate-spin" /></div>
        ) : data.error ? (
          <p className="text-rose-400 text-sm text-center py-6">Failed to load breakdown.</p>
        ) : (
          <>
            <div className="space-y-2">
              {data.buckets.length === 0 ? (
                <p className="text-white/40 italic text-sm text-center py-4">No calls in this window.</p>
              ) : data.buckets.map(b => {
                const meta = STATUS_META[b.status] || { label: b.status, tone: 'bg-white/30' };
                return (
                  <div key={b.status} data-testid={`bucket-${b.status}`}>
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className={`text-white/80 font-medium ${b.is_ungradeable ? 'text-white/50' : ''}`}>
                        {meta.label}
                      </span>
                      <span className="text-white/60 tabular-nums">{b.count} · {b.pct}%</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${meta.tone} transition-all`}
                        style={{ width: `${Math.max(b.pct, b.count > 0 ? 1 : 0)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {data.recent_regrade_jobs?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-2">
                  Recent regrade jobs
                </p>
                <div className="space-y-1.5">
                  {data.recent_regrade_jobs.map(j => (
                    <div key={j.id} className="text-xs flex items-center gap-2" data-testid={`regrade-job-${j.id}`}>
                      <JobIcon status={j.status} />
                      <span className="text-white/70 tabular-nums">
                        {new Date(j.started_at).toLocaleString()}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${
                        j.status === 'running'    ? 'bg-amber-500/20 text-amber-300' :
                        j.status === 'completed'  ? 'bg-emerald-500/20 text-emerald-300' :
                        j.status === 'superseded' ? 'bg-white/10 text-white/40' :
                                                     'bg-rose-500/20 text-rose-300'
                      }`}>{j.status}</span>
                      <span className="text-white/50 ml-auto">
                        target {j.target_count}
                        {j.finished_at && <> · done {formatETA(Math.round((new Date(j.finished_at) - new Date(j.started_at)) / 1000))}</>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <CoachingImpactSection
              impact={data.coaching_impact}
              domainId={domainId}
              onNavigate={handleSparklineNavigate}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default GradingStatusBreakdownModal;
export { formatETA };
